import React, { useState, useEffect } from 'react';
import { Save, Loader2, CheckCircle, ShieldCheck, Banknote, QrCode, CreditCard, Wallet } from 'lucide-react';
import { auth } from '../config/firebase';
import { usuarioService } from '../services/api';

const FORMAS_PAGAMENTO_PADRAO = {
  dinheiro: true,
  pix: true,
  credito: { ativo: true, taxa: 0, modo: 'somar' },
  debito: { ativo: true, taxa: 0, modo: 'somar' },
};

// nome/telefone/endereco/slug/diferenciais não são editáveis aqui, mas precisam ser carregados
// e reenviados junto no salvar — o endpoint de perfil espera o objeto completo.
const FormasPagamentoTab = () => {
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [endereco, setEndereco] = useState('');
  const [slug, setSlug] = useState('');
  const [diferenciais, setDiferenciais] = useState([]);
  const [formasPagamento, setFormasPagamento] = useState(FORMAS_PAGAMENTO_PADRAO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const carregarDados = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const resPerfil = await usuarioService.obterPerfil(user.uid);
        setNome(resPerfil.data.nome || '');
        setTelefone(resPerfil.data.telefone || '');
        setEndereco(resPerfil.data.endereco || '');
        setSlug(resPerfil.data.slug || '');
        setDiferenciais(resPerfil.data.diferenciais || []);
        setFormasPagamento({ ...FORMAS_PAGAMENTO_PADRAO, ...(resPerfil.data.formas_pagamento || {}) });
      } catch (error) {
        console.error("Erro ao carregar perfil:", error);
      } finally {
        setLoading(false);
      }
    };
    carregarDados();
  }, []);

  const alternarFormaSimples = (chave) => {
    setFormasPagamento(prev => ({ ...prev, [chave]: !prev[chave] }));
  };

  const atualizarCartao = (chave, campo, valor) => {
    setFormasPagamento(prev => ({ ...prev, [chave]: { ...prev[chave], [campo]: valor } }));
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);
    try {
      const uid = auth.currentUser.uid;
      await usuarioService.atualizarPerfil(uid, { nome, telefone, slug, endereco, diferenciais, formas_pagamento: formasPagamento });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Erro ao atualizar formas de pagamento:", error);
      alert(error.response?.data?.erro || "Erro ao salvar as alterações.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-14 text-center text-slate-500">
        <Loader2 className="animate-spin text-[#1883FF] mb-4" size={40} />
        <p className="text-[10px] font-black uppercase tracking-[0.2em]">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[620px] animate-fade-in-up">
      <form onSubmit={handleSalvar} className="flex flex-col gap-6">

        <div className="bg-white rounded-[28px] border border-slate-100 shadow-sm p-6">
          <h3 className="text-xs font-black uppercase text-[#1e293b] tracking-wide mb-1">Formas de Pagamento</h3>
          <p className="text-[10px] text-slate-400 font-semibold mb-5">O cliente escolhe uma dessas ao agendar pelo seu link público.</p>

          <div className="grid grid-cols-2 gap-3 mb-3">
            {[
              { chave: 'dinheiro', label: 'Dinheiro', icone: Banknote },
              { chave: 'pix', label: 'Pix', icone: QrCode },
            ].map(({ chave, label, icone: Icone }) => {
              const ativo = !!formasPagamento[chave];
              return (
                <button
                  key={chave}
                  type="button"
                  onClick={() => alternarFormaSimples(chave)}
                  className={`flex flex-col items-center justify-center gap-2 py-4 rounded-2xl border-2 cursor-pointer transition-all ${ativo ? 'bg-[#F5F3FF] border-[#6D28D9] text-[#6D28D9]' : 'bg-[#F8F7FF] border-transparent text-slate-400 hover:border-purple-100'}`}
                >
                  <Icone size={22} />
                  <span className="text-[9px] font-black uppercase tracking-wide">{label}</span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3">
            {[
              { chave: 'credito', label: 'Cartão de Crédito', icone: CreditCard },
              { chave: 'debito', label: 'Cartão de Débito', icone: Wallet },
            ].map(({ chave, label, icone: Icone }) => {
              const cfg = formasPagamento[chave] || { ativo: false, taxa: 0, modo: 'somar' };
              return (
                <div key={chave} className={`rounded-2xl border-2 p-4 transition-all ${cfg.ativo ? 'border-[#6D28D9]/20 bg-[#FAF9FF]' : 'border-transparent bg-[#F8F7FF]'}`}>
                  <button
                    type="button"
                    onClick={() => atualizarCartao(chave, 'ativo', !cfg.ativo)}
                    className="w-full flex items-center justify-between gap-2 cursor-pointer border-none bg-transparent p-0"
                  >
                    <span className={`flex items-center gap-2 text-xs font-black ${cfg.ativo ? 'text-[#6D28D9]' : 'text-slate-400'}`}>
                      <Icone size={18} /> {label}
                    </span>
                    <span className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${cfg.ativo ? 'bg-[#6D28D9]' : 'bg-slate-200'}`}>
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${cfg.ativo ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </span>
                  </button>

                  {cfg.ativo && (
                    <div className="mt-4 flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Taxa</label>
                        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={cfg.taxa}
                            onChange={e => atualizarCartao(chave, 'taxa', e.target.value === '' ? 0 : Number(e.target.value))}
                            className="w-14 border-none outline-none text-xs font-bold text-slate-700 bg-transparent"
                          />
                          <span className="text-xs font-bold text-slate-400">%</span>
                        </div>
                      </div>

                      <div className="flex bg-white rounded-xl border border-slate-200 p-1 gap-1">
                        {[
                          { valor: 'somar', label: 'Repassar ao cliente' },
                          { valor: 'descontar', label: 'Eu absorvo o custo' },
                        ].map(({ valor, label: labelModo }) => (
                          <button
                            key={valor}
                            type="button"
                            onClick={() => atualizarCartao(chave, 'modo', valor)}
                            className={`flex-1 py-2 rounded-lg text-[9px] font-black uppercase tracking-wide cursor-pointer border-none transition-all ${cfg.modo === valor ? 'bg-[#6D28D9] text-white' : 'bg-transparent text-slate-400'}`}
                          >
                            {labelModo}
                          </button>
                        ))}
                      </div>

                      <p className="text-[10px] text-slate-400 font-semibold leading-relaxed ml-1">
                        {cfg.modo === 'descontar'
                          ? `O cliente paga o preço de tabela; o valor que entra no seu Financeiro sai com ${cfg.taxa || 0}% a menos.`
                          : `Ex: um serviço de R$100 fica R$${(100 * (1 + (Number(cfg.taxa) || 0) / 100)).toFixed(2).replace('.', ',')} pro cliente nessa forma.`}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className={`w-full py-4 border-none rounded-2xl text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2.5 transition-all duration-400 cursor-pointer shadow-lg active:scale-98 disabled:opacity-60 disabled:cursor-not-allowed ${success ? 'bg-[#059669] shadow-emerald-600/20' : 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] shadow-[#6D28D9]/20 hover:opacity-90 hover:-translate-y-0.5'}`}
        >
          {saving ? <Loader2 className="animate-spin" size={20} /> : success ? <CheckCircle size={20} /> : <Save size={20} />}
          {saving ? 'Salvando...' : success ? 'Salvo!' : 'Salvar Alterações'}
        </button>

        <div className="p-4 bg-[#f0fdf4] rounded-2xl border border-[#dcfce7] flex items-center gap-3 text-[#166534] text-xs font-semibold leading-relaxed">
          <ShieldCheck size={20} className="text-emerald-600 shrink-0" />
          <p className="m-0">Seus dados estão protegidos pela criptografia de ponta a ponta da <strong>Agenda Sincro</strong>.</p>
        </div>
      </form>
    </div>
  );
};

export default FormasPagamentoTab;
